'use client'

import { useQuery } from '@tanstack/react-query'
import { ChevronRight, Loader2 } from 'lucide-react'
import Link from 'next/link'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { useUser } from '@/contexts/session-context'
import type { AtaliaConversation, AtaliaEnvelope } from '@/lib/atalia/types'
import { AttendanceTranscript } from './attendance-transcript'

const HISTORY_LIMIT = 50
const UNAUTHORIZED_STATUS = 401
const HISTORY_PARAM = 'historico'
const HISTORY_RETURN_URL = `/suporte?${HISTORY_PARAM}=1`
const LOGIN_URL = `/conta/login?redirect_url=${encodeURIComponent(HISTORY_RETURN_URL)}`

const STATUS_LABEL: Record<AtaliaConversation['status'], string> = {
  open: 'Em andamento',
  waiting: 'Aguardando resposta',
  closed: 'Encerrado',
}

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

class UnauthorizedError extends Error {}

async function fetchConversations(): Promise<AtaliaConversation[]> {
  const response = await fetch(
    `/api/atalia/conversations?limit=${HISTORY_LIMIT}`
  )
  if (response.status === UNAUTHORIZED_STATUS) {
    throw new UnauthorizedError('Não autenticado')
  }

  const body = (await response.json()) as AtaliaEnvelope<AtaliaConversation[]>
  if (!(response.ok && body.success)) {
    throw new Error(body.error ?? 'Erro ao buscar conversas')
  }
  return body.data ?? []
}

interface HistoryListProps {
  conversations: AtaliaConversation[]
  onSelect: (conversation: AtaliaConversation) => void
}

function HistoryList({ conversations, onSelect }: HistoryListProps) {
  if (conversations.length === 0) {
    return (
      <p className="py-6 text-center text-[#656566] text-sm">
        Você ainda não possui atendimentos anteriores.
      </p>
    )
  }

  return (
    <ul className="flex max-h-96 flex-col gap-2 overflow-y-auto">
      {conversations.map((conversation) => (
        <li key={conversation.id}>
          <button
            aria-label={`Ver mensagens do atendimento #${conversation.id}`}
            className="flex w-full items-center justify-between gap-4 rounded-lg border bg-primary-50 px-4 py-3 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 [@media(hover:hover)_and_(pointer:fine)]:hover:border-primary-400"
            onClick={() => onSelect(conversation)}
            type="button"
          >
            <span>
              <span className="block font-medium text-[#41196B] text-sm">
                Atendimento #{conversation.id}
              </span>
              <time
                className="text-[#656566] text-xs"
                dateTime={conversation.created_at}
              >
                {dateFormatter.format(new Date(conversation.created_at))}
              </time>
            </span>
            <span className="flex shrink-0 items-center gap-2 text-[#656566] text-xs">
              {STATUS_LABEL[conversation.status]}
              <ChevronRight aria-hidden="true" className="size-4" />
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}

interface HistoryBodyProps {
  isOpen: boolean
  onSelect: (conversation: AtaliaConversation) => void
}

function HistoryBody({ isOpen, onSelect }: HistoryBodyProps) {
  const { user } = useUser()
  const { data, error, isPending, refetch } = useQuery({
    queryKey: ['atalia', 'conversations', user?.reference ?? 'anonymous'],
    queryFn: fetchConversations,
    enabled: isOpen,
    retry: false,
  })

  if (isPending) {
    return (
      <div className="flex flex-col gap-2" role="status">
        <p className="flex items-center justify-center gap-2 py-2 text-[#656566] text-sm">
          <Loader2 aria-hidden="true" className="size-4 animate-spin" />
          Carregando histórico...
        </p>
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    )
  }

  if (error instanceof UnauthorizedError) {
    return (
      <div className="flex flex-col items-center gap-3 py-4 text-center">
        <p className="text-[#656566] text-sm">
          Entre na sua conta para consultar seus atendimentos anteriores.
        </p>
        <Button asChild>
          <Link href={LOGIN_URL}>Entrar</Link>
        </Button>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 py-4 text-center">
        <p className="text-[#656566] text-sm">
          Não foi possível carregar seu histórico agora.
        </p>
        <Button onClick={() => refetch()} type="button" variant="outline">
          Tentar novamente
        </Button>
      </div>
    )
  }

  return <HistoryList conversations={data ?? []} onSelect={onSelect} />
}

interface AttendanceHistoryDialogProps {
  children: ReactNode
  className: string
}

export function AttendanceHistoryDialog({
  children,
  className,
}: AttendanceHistoryDialogProps) {
  const { isSignedIn } = useUser()
  const hasAutoOpenedRef = useRef(false)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedConversation, setSelectedConversation] =
    useState<AtaliaConversation | null>(null)

  function handleOpenChange(open: boolean) {
    setIsOpen(open)
    if (!open) {
      setSelectedConversation(null)
    }
  }

  // Logado: o histórico abre sozinho (uma vez) ao entrar em /suporte.
  // Deslogado: não abre; o usuário abre pelo card e vê o convite para entrar.
  useEffect(() => {
    if (isSignedIn && !hasAutoOpenedRef.current) {
      hasAutoOpenedRef.current = true
      setIsOpen(true)
    }
  }, [isSignedIn])

  // Reabre o histórico ao voltar do login (?historico=1)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get(HISTORY_PARAM) !== '1') {
      return
    }
    setIsOpen(true)
    params.delete(HISTORY_PARAM)
    const query = params.toString()
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}`
    )
  }, [])

  return (
    <Dialog onOpenChange={handleOpenChange} open={isOpen}>
      <DialogTrigger asChild>
        <button className={className} type="button">
          {children}
        </button>
      </DialogTrigger>

      <DialogContent className="max-w-sm sm:max-w-lg">
        <DialogHeader className="text-left">
          <DialogTitle className="text-primary-900 text-xl">
            Histórico de Atendimentos
          </DialogTitle>
          <DialogDescription>
            Seus atendimentos anteriores e as respectivas datas.
          </DialogDescription>
        </DialogHeader>

        {selectedConversation ? (
          <AttendanceTranscript
            conversation={selectedConversation}
            dateLabel={dateFormatter.format(
              new Date(selectedConversation.created_at)
            )}
            onBack={() => setSelectedConversation(null)}
            statusLabel={STATUS_LABEL[selectedConversation.status]}
          />
        ) : (
          <HistoryBody isOpen={isOpen} onSelect={setSelectedConversation} />
        )}
      </DialogContent>
    </Dialog>
  )
}
